import { ProductSizes } from '@api/returns/types'
import { Values, ExchangesSizeOption } from '@components/returns/types'
import { topsSizeList } from './data'
import styles from './styles.module.scss'
import trackError from '@helpers/trackError'
import { Option } from '@components/ui/formElements/form/selectFields/SelectField'
import { RawSelectField } from '@components/ui/formElements/form/selectFields/RawSelectField'

type SelectedValue = { [id: string]: string | number }

type ListItem = {
  label: string
  value: string | number
  available?: boolean
  flag?: React.ReactNode
}
type List = ListItem[]

type DropdownSizeOptionsListProps = {
  sizeOptions: ExchangesSizeOption
  setSelection: (args: {
    sizes: Values['sizes'] & { available?: boolean }
  }) => void
  sizes: Partial<ProductSizes>
  values: Values
}

type TopsSize = keyof typeof topsSizeList
type TopsCut = keyof (typeof topsSizeList)['S']
type TopsLength = keyof (typeof topsSizeList)['S']['Straight']

const checkLengthAvailabilityUtil = (
  selectedValue: SelectedValue,
  values: Values
) => {
  try {
    if (Object.keys(values.sizes).length !== 0) {
      const { cut, length, size } = values.sizes as {
        cut: TopsCut
        length: TopsLength
        size: TopsSize
      }
      let topSizes = size && cut && length && topsSizeList[size]

      if (topSizes) {
        let checkLengthForSizeAndCut = cut && length && topSizes[cut][length]

        const selection = Object.keys(selectedValue).toString()

        switch (selection) {
          case 'size':
            topSizes = size && topsSizeList[selectedValue.size as TopsSize]
            checkLengthForSizeAndCut = cut && length && topSizes[cut][length]
            break

          case 'cut':
            topSizes = size && topsSizeList[size]
            checkLengthForSizeAndCut =
              cut && length && topSizes[selectedValue.cut as TopsCut][length]
            break

          case 'length':
            topSizes = size && topsSizeList[size]
            checkLengthForSizeAndCut =
              cut && length && topSizes[cut][selectedValue.length as TopsLength]
            break

          default:
            return { sizes: values.sizes, available: true }
        }
        return { sizes: values.sizes, available: checkLengthForSizeAndCut }
      }

      return { sizes: values.sizes, available: true }
    }
  } catch (error) {
    trackError(new Error('Failed to build returns modal sizes form'), {
      sizes: values.sizes,
      error: error as Error,
      section: 'Returns'
    })
    return { sizes: values.sizes, available: false }
  }

  return { sizes: values.sizes, available: false }
}

const updateLengthList = (
  values: Values,
  currentValue: string
): List[number] => {
  const { available } = checkLengthAvailabilityUtil(
    {
      length: currentValue
    },
    values
  )

  return {
    value: currentValue,
    label: currentValue,
    available: available
  }
}

const updateSize = (selectedValue: SelectedValue, values: Values) => {
  const { sizes, available } = checkLengthAvailabilityUtil(
    selectedValue,
    values
  )
  return {
    sizes: { ...sizes, available: available, ...selectedValue }
  }
}

export const getSizeOptionsList = (
  sizeOptions: ExchangesSizeOption,
  values: Values
) =>
  sizeOptions.answers.map(currentValue => {
    if (/Short|Regular|Long/.test(currentValue)) {
      return updateLengthList(values, currentValue)
    }
    const value = currentValue.replace('Build ', '')
    return { value, label: value, available: true }
  })

export const getAvailableSizeOptionsList = (
  sizeOptions: ExchangesSizeOption,
  values: Values
) => {
  const { cut, size } = values.sizes as {
    cut?: TopsCut
    size?: TopsSize
  }

  return getSizeOptionsList(sizeOptions, values).map(option =>
    /Short|Regular|Long/.test(String(option.value)) && size && cut
      ? {
          ...option,
          available:
            topsSizeList[size]?.[cut]?.[option.value as TopsLength] ?? false
        }
      : option
  )
}

function DropdownSizeOptionsList({
  sizeOptions,
  sizes,
  values,
  setSelection
}: DropdownSizeOptionsListProps) {
  const id = sizeOptions.id

  const list = getSizeOptionsList(sizeOptions, values)

  const selectedValue = String(values.sizes?.[id] ?? sizes?.[id] ?? '')

  const handleChange = (nextValue: string) => {
    setSelection(updateSize({ [id]: nextValue }, values))
  }

  const shouldBeInline = sizeOptions.id === 'taper' || sizeOptions.id === 'leg'

  return (
    <div
      className={`${styles.returnsForm__element} ${
        shouldBeInline ? styles.inline : ''
      }`}
    >
      <RawSelectField
        id={sizeOptions.id}
        options={list as unknown as Option[]}
        name={sizeOptions.question}
        placeholder={`Select ${sizeOptions.id}`}
        settings={{ label: { visible: true } }}
        value={selectedValue}
        setValue={handleChange}
      />
    </div>
  )
}

export default DropdownSizeOptionsList
